import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
    Trophy, Calendar, MapPin, TrendingUp, ChevronLeft, 
    BarChart3, Target, Percent, Clock, User, Award,
    ChevronRight, Star, Zap
} from 'lucide-react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Experience Score Component
const ExperienceScore = ({ runs, wins }) => {
    if (!runs || runs === 0) return null;
    
    const winRate = Math.round((wins / runs) * 100);
    let level = 'Novice';
    let color = 'bg-slate-500';
    
    if (runs >= 20) {
        level = 'Veteran';
        color = 'bg-purple-500';
    } else if (runs >= 10) {
        level = 'Experienced';
        color = 'bg-blue-500';
    } else if (runs >= 5) {
        level = 'Developing';
        color = 'bg-emerald-500';
    }
    
    return (
        <div className="flex items-center gap-2">
            <Badge className={`${color} text-white`}>
                <Zap className="h-3 w-3 mr-1" />
                {level}
            </Badge>
            <span className="text-sm text-muted-foreground">
                {runs} runs • {wins} wins ({winRate}%)
            </span>
        </div>
    );
};

// Form String Visualization
const FormVisualization = ({ events, horseName }) => {
    if (!events || events.length === 0) return null;
    
    // Get last 10 results for our horse specifically
    const recentResults = events.slice(0, 10).map(event => {
        // First try to find our horse's entry
        if (event.ourHorseEntry) {
            const position = event.ourHorseEntry.result?.position;
            return position ? parseInt(position) : null;
        }
        // Horse finished outside top 3 - return null (unknown position)
        return null;
    }).filter(p => p !== null);
    
    if (recentResults.length === 0) {
        return (
            <div className="text-sm text-muted-foreground">
                <p>Form visualization unavailable - horse finished outside top 3 in recent races.</p>
            </div>
        );
    }
    
    const maxPos = Math.max(...recentResults, 10);
    
    return (
        <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Recent Form (Last {recentResults.length} top-3 finishes)</p>
            <div className="flex items-end gap-1 h-20">
                {recentResults.map((pos, idx) => {
                    const height = Math.max(20, 100 - (pos - 1) * 10);
                    const isWin = pos === 1;
                    const isPlace = pos <= 3;
                    
                    return (
                        <div
                            key={idx}
                            className="flex flex-col items-center gap-1 flex-1"
                        >
                            <div
                                className={`w-full rounded-t transition-all ${
                                    isWin ? 'bg-amber-500' : isPlace ? 'bg-emerald-500' : 'bg-muted'
                                }`}
                                style={{ height: `${height}%` }}
                            />
                            <span className={`text-xs font-data ${isWin ? 'text-amber-500 font-bold' : ''}`}>
                                {pos}
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="flex gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-amber-500 rounded" /> Win
                </span>
                <span className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-emerald-500 rounded" /> Place (2nd-3rd)
                </span>
                <span className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-muted rounded" /> Other
                </span>
            </div>
        </div>
    );
};

// Career Stats Card
const CareerStatsCard = ({ stats }) => {
    if (!stats) return null;
    
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/20">
                <CardContent className="p-4 text-center">
                    <Trophy className="h-6 w-6 mx-auto text-amber-500 mb-2" />
                    <p className="font-data text-3xl font-bold">{stats.wins}</p>
                    <p className="text-sm text-muted-foreground">Wins</p>
                </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/20">
                <CardContent className="p-4 text-center">
                    <Target className="h-6 w-6 mx-auto text-emerald-500 mb-2" />
                    <p className="font-data text-3xl font-bold">{stats.places}</p>
                    <p className="text-sm text-muted-foreground">Places</p>
                </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
                <CardContent className="p-4 text-center">
                    <BarChart3 className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                    <p className="font-data text-3xl font-bold">{stats.totalRuns}</p>
                    <p className="text-sm text-muted-foreground">Career Runs</p>
                </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
                <CardContent className="p-4 text-center">
                    <Percent className="h-6 w-6 mx-auto text-purple-500 mb-2" />
                    <p className="font-data text-3xl font-bold">{stats.winRate}%</p>
                    <p className="text-sm text-muted-foreground">Win Rate</p>
                </CardContent>
            </Card>
        </div>
    );
};

// Race History Row
const RaceHistoryRow = ({ event, index }) => {
    const stage = event.stage || {};
    // Use our horse's entry if available, otherwise show as "outside top 3"
    const entrant = event.ourHorseEntry || {};
    const result = entrant.result || {};
    const position = result.position;
    const sp = entrant.betting?.startingprice?.price;
    const odds = sp ? `${sp.numerator}/${sp.denominator}` : 'N/A';
    
    const isWin = position === '1' || position === 1;
    const isPlace = position && parseInt(position) <= 3;
    const isOutsideTop3 = !event.ourHorseEntry;
    
    // Get jockey and trainer
    const participants = entrant.participants || [];
    const jockey = participants.find(p => p.participant?.type === 'jockey')?.participant?.name;
    const trainer = participants.find(p => p.participant?.type === 'trainer')?.participant?.name;
    
    // Get distance and class
    const distance = event.meta?.['event:distance:string']?.value;
    const raceClass = event.meta?.['event:class']?.value;
    
    return (
        <div 
            className={`p-4 border-b last:border-b-0 hover:bg-muted/50 transition-colors ${
                isWin ? 'bg-amber-500/5' : isOutsideTop3 ? 'bg-muted/30' : ''
            }`}
        >
            <div className="flex items-start gap-4">
                {/* Position Badge */}
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${
                    isWin ? 'bg-amber-500 text-white' : 
                    isPlace ? 'bg-emerald-500/20 text-emerald-600' : 
                    isOutsideTop3 ? 'bg-muted/50 text-muted-foreground' :
                    'bg-muted text-muted-foreground'
                }`}>
                    <span className="font-data text-xl font-bold">
                        {isOutsideTop3 ? '4+' : (position || '-')}
                    </span>
                </div>
                
                {/* Race Details */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold">{stage.name}</span>
                        <Badge variant="outline" className="font-data text-xs">
                            {format(parseISO(event.start), 'dd MMM yyyy')}
                        </Badge>
                        {isWin && (
                            <Badge className="bg-amber-500 text-white gap-1">
                                <Trophy className="h-3 w-3" />
                                Winner
                            </Badge>
                        )}
                        {isOutsideTop3 && (
                            <Badge variant="secondary" className="text-xs">
                                Outside Top 3
                            </Badge>
                        )}
                    </div>
                    
                    <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                        {event.title}
                    </p>
                    
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {distance && (
                            <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {distance}
                            </span>
                        )}
                        {raceClass && (
                            <span>Class {raceClass}</span>
                        )}
                        {jockey && (
                            <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {jockey}
                            </span>
                        )}
                        {trainer && (
                            <span className="flex items-center gap-1">
                                <Award className="h-3 w-3" />
                                {trainer}
                            </span>
                        )}
                    </div>
                </div>
                
                {/* Odds */}
                <div className="text-right shrink-0">
                    <p className="font-data text-lg font-semibold text-primary">{odds}</p>
                    <p className="text-xs text-muted-foreground">SP</p>
                </div>
            </div>
        </div>
    );
};

export default function HorseProfilePage() {
    const { horseId } = useParams();
    const [horse, setHorse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchHorseData = async () => {
            setLoading(true);
            setError(null);
            try {
                // First, fetch the entrant details to get the actual horse name
                const entrantRes = await axios.get(`${API}/racing/entrants/${horseId}`);
                const entrantData = entrantRes.data;
                
                // Get horse name from entrant details
                const horseName = entrantData.name || 'Unknown Horse';
                
                // Get trainer from participants
                const trainerParticipant = entrantData.participants?.find(p => p.participant?.type === 'trainer');
                const trainer = trainerParticipant?.participant?.name;
                
                // Get form figures for stats calculation
                const meta = entrantData.meta || {};
                const formFigures = meta['entrant:formfigures:flatsix']?.figures ||
                                   meta['entrant:formfigures:jumpsix']?.figures ||
                                   meta['entrant:form']?.value || '';
                
                // Now fetch career history
                const formRes = await axios.get(`${API}/racing/entrants/${horseId}/form`);
                const formData = formRes.data;
                const events = formData.events || [];
                
                // Calculate stats by finding OUR horse in each event
                // The form endpoint returns events the horse ran in, but entrants
                // only shows top 3 finishers. We need to match by horse name.
                let wins = 0;
                let places = 0;
                
                // Process events and find our horse's results
                const processedEvents = events.map(event => {
                    // Look for our horse in the entrants by matching participant name
                    let ourHorseEntry = null;
                    for (const entrant of (event.entrants || [])) {
                        const horseParticipant = entrant.participants?.find(
                            p => p.participant?.type === 'horse' && 
                                 p.participant?.name?.toLowerCase() === horseName.toLowerCase()
                        );
                        if (horseParticipant) {
                            ourHorseEntry = entrant;
                            break;
                        }
                    }
                    
                    // If we found our horse, use that result
                    if (ourHorseEntry) {
                        const pos = ourHorseEntry.result?.position;
                        if (pos) {
                            const posNum = parseInt(pos);
                            if (posNum === 1) wins++;
                            if (posNum <= 3) places++;
                        }
                        return { ...event, ourHorseEntry };
                    }
                    
                    // Horse finished outside top 3 - we don't have their exact position
                    return { ...event, ourHorseEntry: null };
                });
                
                // If we didn't find many results from form history, use form figures
                if (wins === 0 && formFigures) {
                    for (const char of formFigures) {
                        if (char === '1') {
                            wins++;
                            places++;
                        } else if (char === '2' || char === '3') {
                            places++;
                        }
                    }
                }
                
                setHorse({
                    id: horseId,
                    name: horseName,
                    trainer,
                    formFigures,
                    events: processedEvents,
                    stats: {
                        totalRuns: events.length,
                        wins,
                        places,
                        winRate: events.length > 0 ? Math.round((wins / events.length) * 100) : 0,
                        placeRate: events.length > 0 ? Math.round((places / events.length) * 100) : 0
                    }
                });
            } catch (err) {
                console.error('Failed to fetch horse data:', err);
                setError('Failed to load horse profile');
            } finally {
                setLoading(false);
            }
        };
        
        fetchHorseData();
    }, [horseId]);

    if (loading) {
        return (
            <div className="min-h-screen pb-20 md:pb-8">
                <div className="container-app py-6">
                    <Skeleton className="h-8 w-48 mb-4" />
                    <Skeleton className="h-32 w-full mb-6" />
                    <div className="grid grid-cols-4 gap-4 mb-6">
                        {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
                    </div>
                    <Skeleton className="h-64 w-full" />
                </div>
            </div>
        );
    }

    if (error || !horse) {
        return (
            <div className="min-h-screen pb-20 md:pb-8">
                <div className="container-app py-6 text-center">
                    <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h2 className="font-heading text-xl font-semibold mb-2">Horse not found</h2>
                    <p className="text-muted-foreground mb-4">{error || 'Unable to load horse profile'}</p>
                    <Link to="/">
                        <Button className="btn-pill gap-2">
                            <ChevronLeft className="h-4 w-4" />
                            Back to Home
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-20 md:pb-8">
            <div className="container-app py-6">
                {/* Back Button */}
                <button 
                    onClick={() => window.history.back()}
                    className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground mb-4"
                >
                    <ChevronLeft className="h-4 w-4" />
                    Back
                </button>

                {/* Horse Header */}
                <div className="mb-8">
                    <div className="flex items-start gap-4 mb-4">
                        <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Trophy className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold mb-1 page-title">{horse.name}</h1>
                            <ExperienceScore runs={horse.stats.totalRuns} wins={horse.stats.wins} />
                            {horse.trainer && (
                                <p className="text-sm text-muted-foreground mt-1">
                                    <Award className="h-4 w-4 inline mr-1" />
                                    Trainer: {horse.trainer}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Career Stats */}
                <div className="mb-8">
                    <h2 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-primary" />
                        Career Statistics
                    </h2>
                    <CareerStatsCard stats={horse.stats} />
                </div>

                {/* Win Rate Progress */}
                <Card className="mb-8">
                    <CardContent className="p-6">
                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm font-medium">Win Rate</span>
                                    <span className="font-data font-semibold text-amber-500">{horse.stats.winRate}%</span>
                                </div>
                                <Progress value={horse.stats.winRate} className="h-2" />
                            </div>
                            <div>
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm font-medium">Place Rate (Top 3)</span>
                                    <span className="font-data font-semibold text-emerald-500">{horse.stats.placeRate}%</span>
                                </div>
                                <Progress value={horse.stats.placeRate} className="h-2 [&>div]:bg-emerald-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Form Visualization */}
                {horse.events.length > 0 && (
                    <Card className="mb-8">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                                <TrendingUp className="h-5 w-5 text-primary" />
                                Form Chart
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <FormVisualization events={horse.events} horseName={horse.name} />
                        </CardContent>
                    </Card>
                )}

                {/* Race History */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Calendar className="h-5 w-5 text-primary" />
                            Race History ({horse.events.length} runs)
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {horse.events.length === 0 ? (
                            <div className="p-8 text-center">
                                <Clock className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                                <p className="text-muted-foreground">No race history available</p>
                            </div>
                        ) : (
                            <div className="divide-y">
                                {horse.events.map((event, idx) => (
                                    <RaceHistoryRow key={event.id || idx} event={event} index={idx} />
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
